/**
 * @fileoverview Search Filter Component
 * @author Min Hwang (mhwang@yext.com)
 */
// @ts-nocheck
goog.declareModuleId('yext.ui.components.filters.searchfilter');
import {yext} from '/ui/lib/msg';

import * as styles from '/ui/components/filters/filterpill.module.scss';
import SearchSvg from '/ui/icons/inline/search.svg';


const searchIcon = <SearchSvg className={styles.searchIcon}/>;

function SearchFilterInput({
  searchTerm,
  onChange,
  onClose,
  onApply,
  open,
  placeholder,
  tid,
  pendoId,
}) {
  const inputRef = React.useRef();
  React.useEffect(() => {
    if (open) {
      inputRef.current.focus();
    }
  }, [open, inputRef]);
  return (
    <div className={styles.searchInputWrapper}>
      {searchIcon}
      <input
        ref={inputRef}
        value={searchTerm}
        placeholder={placeholder || yext.msg('Search by Name or ID')}
        type="text"
        onChange={e => onChange(e.target.value)}
        className={styles.searchInput}
        tid={tid}
        data-pendo={pendoId}
        onBlur={e => onClose()}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            onApply();
          }
          if (e.key === 'Escape') {
            onClose();
          }
        }}
      />
    </div>
  );
}

/**
 * @typedef {{
 *   searchTerm?: string,
 *   disabled?: (boolean|null),
 *   onChange: (function(string):void),
 *   onApply: (function():void),
 *   onCancel: (function():void),
 *   onClear: (function():void),
 *   isOpen?: boolean,
 *   setIsOpen?: (function(boolean):void),
 *   tid?: string,
 *   pendoId?: string,
 *   placeholder?: string,
 *   pillPlaceholder?: string,
 * }}
 */
export let SearchFilterPillProps;

/**
 * @param {SearchFilterPillProps} props
 * @return {React.ReactElement}
 */
export function SearchFilterPill({
  searchTerm,
  disabled = false,
  onChange,
  onCancel,
  onApply,
  onClear,
  isOpen: externalIsOpen,
  setIsOpen: externalSetIsOpen,
  tid,
  pendoId,
  placeholder,
  pillPlaceholder,
}) {
  // Allow the component to be used in either controlled or uncontrolled form for isOpen state,
  // based on whether or not externalIsOpen and externalSetIsOpen are being passed.
  const [internalIsOpen, internalSetIsOpen] = React.useState(false);
  const isOpen = typeof(externalIsOpen) === 'undefined' ? internalIsOpen : externalIsOpen;
  const setIsOpen = React.useCallback(isOpen => {
    internalSetIsOpen(isOpen);
    externalSetIsOpen && externalSetIsOpen(isOpen);
  }, [internalSetIsOpen, externalSetIsOpen]);

  const applyAndClose = () => {
    onApply();
    setIsOpen(false);
  };
  const cancelAndClose = () => {
    onCancel();
    setIsOpen(false);
  };

  const clearIcon = (
    <button
      type="button"
      onClick={() => !disabled && onClear()}
      disabled={disabled}
      className={styles.deleteButton}
      tid={tid && tid + '-clear'}
      data-pendo={pendoId && pendoId + '-clear'}
    >
      <span
        className="icon-small-x"
        aria-label={yext.msg('Remove filter')}
      />
    </button>
  );
  return (
    <div className={styles.pillContainer}>
      <div
        className={
          (!isOpen && searchTerm !== '')
            ? styles.searchPill
            : styles.emptyOrOpenSearchPill
        }
        tid={tid}
      >
        {
          !isOpen
            && <>
              <button
                type="button"
                disabled={disabled}
                className={searchTerm !== '' ? styles.deletableLabelButton : styles.labelButton}
                onClick={() => setIsOpen(true)}
                data-pendo={pendoId}
              >
                {searchIcon}
                {pillPlaceholder || yext.msg('Search')}
                {searchTerm !== '' && <span>: {searchTerm}</span>}
              </button>
              {searchTerm !== '' && clearIcon}
            </>
        }
        {
          isOpen
            && <SearchFilterInput
              searchTerm={searchTerm}
              onChange={onChange}
              onClose={cancelAndClose}
              onApply={applyAndClose}
              open={isOpen}
              placeholder={placeholder}
              pendoId={pendoId && pendoId + '-input'}
              tid={tid && tid + '-input'}
            />
        }
      </div>
    </div>
  );
}
